import React, { useState, useEffect } from 'react';
import { Container, Button, Alert, Spinner, Table, Form, Row, Col, Card } from 'react-bootstrap';
import SelectControl from './common/SelectControl';
import DarkHeader from './common/DarkHeader';

function CfoBurnReport({ onBack, token, onLogout, onSettings }) {
    const [loading, setLoading] = useState(true);
    const [accessGranted, setAccessGranted] = useState(false);
    const [error, setError] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [mondays, setMondays] = useState([]);
    const [showPounds, setShowPounds] = useState(false);
    const [loadingReport, setLoadingReport] = useState(false);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [reportType, setReportType] = useState(3);
    const [activeColumn, setActiveColumn] = useState(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);

    useEffect(() => {
        checkAccess();
    }, []);

    const checkAccess = async () => {
        try {
            setLoading(true);
            const response = await fetch(`/api/reports/cfo-burn.php?action=access-check`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                setAccessGranted(true);
                setCompanyInfo(data);
                // Initialize date filters to last month but DO NOT auto-load results
                const today = new Date();
                const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                setDateFrom(fmtLocal(lastMonthStart));
                setDateTo(fmtLocal(lastMonthEnd));
            } else {
                setError(data.error || 'Access denied');
            }
        } catch (err) {
            setError('Failed to check access: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const loadReportData = async (companyId, fromDate = null, toDate = null, type = null) => {
        try {
            setLoadingReport(true);
            let from, to;
            if (fromDate && toDate) {
                from = fromDate;
                to = toDate;
            } else {
                const today = new Date();
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastDayOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
                from = lastMonth.toISOString().slice(0, 10).replace(/-/g, '');
                to = lastDayOfLastMonth.toISOString().slice(0, 10).replace(/-/g, '');
            }

            const response = await fetch(`/api/reports/cfo-burn.php?action=report-data&company_id=${companyId}&from_date=${from}&to_date=${to}&report_type=${type || reportType}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (data.success) {
                setReportData(data.data);
                setMondays(data.mondays);
            } else {
                setError(data.error || 'Failed to load report data');
            }
        } catch (err) {
            setError('Failed to load report data: ' + err.message);
        } finally {
            setLoadingReport(false);
        }
    };

    // Local date formatter to avoid timezone issues
    const fmtLocal = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const setQuickDate = (months) => {
        const today = new Date();
        let from, to;
        
        if (months === 0) { // This month
            from = new Date(today.getFullYear(), today.getMonth(), 1);
            to = today;
            setDateFrom(fmtLocal(from));
            setDateTo(fmtLocal(to));
            return;
        }
        
        if (months === 1) { // Last month
            from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            to = new Date(today.getFullYear(), today.getMonth(), 0);
            setDateFrom(fmtLocal(from));
            setDateTo(fmtLocal(to));
            return;
        }
        
        if (months === 3) { // Last 3 months
            from = new Date(today.getFullYear(), today.getMonth() - 3, 1);
            to = today;
            setDateFrom(fmtLocal(from));
            setDateTo(fmtLocal(to));
            return;
        }
    };

    const handleCustomDateSubmit = (e) => {
        e.preventDefault();
        if (dateFrom && dateTo && companyInfo) {
            setHasSubmitted(true);
            const fromStr = dateFrom.replace(/-/g, '');
            const toStr = dateTo.replace(/-/g, '');
            loadReportData(companyInfo.company_id, fromStr, toStr);
        }
    };

    const handleReportTypeChange = (e) => {
        const newType = parseInt(e.target.value);
        setReportType(newType);
        // Do not auto-load; only load on submit
    };

    const handleColumnClick = (columnId) => {
        setActiveColumn(activeColumn === columnId ? null : columnId);
    };

    const renderTableBody = () => {
        if (!reportData) return null;

        const rows = [];
        let totals = 0;
        const monthTotals = {};

        Object.entries(reportData).forEach(([groupName, approvers]) => {
            Object.entries(approvers).forEach(([approverName, experts]) => {
                Object.entries(experts).forEach(([expertName, bookings]) => {
                    // Calculate approval status for this expert/approver combination
                    const approvalStatuses = bookings.map(b => b.approved);
                    const allApproved = approvalStatuses.every(status => status === 'yes');
                    const noneApproved = approvalStatuses.every(status => status === 'no');
                    const approvalClass = allApproved ? 'yes' : noneApproved ? 'no' : 'both';

                    // Calculate total for this expert/approver
                    const expertTotal = bookings.reduce((sum, booking) => sum + booking.amount, 0);
                    totals += expertTotal;

                    // Organize bookings by week
                    const weeklyData = {};
                    bookings.forEach(booking => {
                        const weekDate = booking.week_date;
                        if (!weeklyData[weekDate]) {
                            weeklyData[weekDate] = { amount: booking.amount, class: booking.approved };
                        } else {
                            weeklyData[weekDate].amount += booking.amount;
                            if (weeklyData[weekDate].class !== booking.approved) {
                                weeklyData[weekDate].class = 'both';
                            }
                        }
                    });

                    rows.push(
                        <tr key={`${groupName}-${approverName}-${expertName}`}>
                            <td className="group">{groupName}</td>
                            <td className="name">{expertName}</td>
                            <td className="name">
                                <span style={{color: approvalClass === 'yes' ? 'green' : approvalClass === 'no' ? 'red' : 'orange'}}>
                                    {approverName}
                                </span>
                            </td>
                            <td>est</td>
                            <td title="total">
                                {showPounds ? `£${expertTotal * 5}` : expertTotal}
                            </td>
                            {mondays.map((monday, index) => {
                                const weekData = weeklyData[monday];
                                if (weekData) {
                                    if (!monthTotals[monday]) monthTotals[monday] = 0;
                                    monthTotals[monday] += weekData.amount;
                                    
                                    const statusClass = weekData.class === 'yes' ? 'status-yes' : 
                                                       weekData.class === 'no' ? 'status-no' : 'status-pending';
                                    
                                    return (
                                        <td 
                                            key={index} 
                                            className={`date-col ${activeColumn === index + 1 ? 'active' : ''}`} 
                                            data-id={index + 1}
                                            onClick={() => handleColumnClick(index + 1)}
                                            style={{cursor: 'pointer'}}
                                        >
                                            <span className={statusClass}>
                                                {showPounds ? `£${weekData.amount * 5}` : weekData.amount}
                                            </span>
                                        </td>
                                    );
                                } else {
                                    if (!monthTotals[monday]) monthTotals[monday] = 0;
                                    return (
                                        <td 
                                            key={index} 
                                            className={`date-col ${activeColumn === index + 1 ? 'active' : ''}`} 
                                            data-id={index + 1}
                                            onClick={() => handleColumnClick(index + 1)}
                                            style={{cursor: 'pointer'}}
                                        ></td>
                                    );
                                }
                            })}
                        </tr>
                    );
                });
            });
        });

        return rows;
    };

    const renderTotalsRow = () => {
        if (!reportData) return null;

        // Calculate totals
        let grandTotal = 0;
        const monthTotals = {};

        Object.values(reportData).forEach(approvers => {
            Object.values(approvers).forEach(experts => {
                Object.values(experts).forEach(bookings => {
                    bookings.forEach(booking => {
                        grandTotal += booking.amount;
                        if (!monthTotals[booking.week_date]) {
                            monthTotals[booking.week_date] = 0;
                        }
                        monthTotals[booking.week_date] += booking.amount;
                    });
                });
            });
        });

        return (
            <tr className={`table-totals-row ${showPounds ? 'pounds-holder' : 'units-holder'}`}>
                <td><b>Totals ({showPounds ? 'GBP' : 'Units'})</b></td>
                <td>-</td>
                <td>-</td>
                <td>-</td>
                <td><b>{showPounds ? `£${grandTotal * 5}` : grandTotal}</b></td>
                {mondays.map((monday, index) => (
                    <td key={index}>
                        <b>{showPounds ? `£${(monthTotals[monday] || 0) * 5}` : (monthTotals[monday] || 0)}</b>
                    </td>
                ))}
            </tr>
        );
    };

    if (loading) {
        return (
            <div className="page-container">
                <div className="bg-blur-indigo"></div>
                <div className="bg-blur-fuchsia"></div>
                <DarkHeader
                    title="CFO Burn Report"
                    subtitle="Loading..."
                    showBackButton={true}
                    onBack={onBack}
                    backButtonText="Back to Reports"
                />
                <Container fluid style={{ position: 'relative', zIndex: 10 }}>
                    <div className="text-center">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </Spinner>
                        <p className="mt-2 theme-text">Checking access...</p>
                    </div>
                </Container>
            </div>
        );
    }

    if (error) {
        return (
            <div className="page-container">
                <div className="bg-blur-indigo"></div>
                <div className="bg-blur-fuchsia"></div>
                <DarkHeader
                    title="CFO Burn Report"
                    subtitle="Access Denied"
                    showBackButton={true}
                    onBack={onBack}
                    backButtonText="Back to Reports"
                />
                <Container fluid style={{ position: 'relative', zIndex: 10 }}>
                    <Alert variant="danger">
                        <h4>Access Denied</h4>
                        <p>{error}</p>
                    </Alert>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="bg-blur-indigo"></div>
            <div className="bg-blur-fuchsia"></div>
            
            <DarkHeader
                title="Full CFO Burn Report"
                subtitle={companyInfo ? `Company: ${companyInfo.company_name}` : "Financial burn analysis"}
                showBackButton={true}
                onBack={onBack}
                backButtonText="Back to Reports"
            />

            <Container fluid style={{ position: 'relative', zIndex: 10 }}>
                
                {/* Date Filters */}
                <Card className="glass mb-4">
                    <Card.Body>
                        <h5 className="mb-3 theme-text">Date Range</h5>
                        <Form.Group className="mb-3">
                        <div>
                            <Form.Check 
                                inline 
                                type="radio" 
                                name="quickDate" 
                                label="This Month" 
                                onChange={() => setQuickDate(0)}
                            />
                            <Form.Check 
                                inline 
                                type="radio" 
                                name="quickDate" 
                                label="Last Month" 
                                defaultChecked
                                onChange={() => setQuickDate(1)}
                            />
                            <Form.Check 
                                inline 
                                type="radio" 
                                name="quickDate" 
                                label="Last Three Months" 
                                onChange={() => setQuickDate(3)}
                            />
                        </div>
                    </Form.Group>
                    
                    <Form onSubmit={handleCustomDateSubmit}>
                        <Row className="align-items-end">
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>From Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={dateFrom}
                                        onChange={(e) => setDateFrom(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>To Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={dateTo}
                                        onChange={(e) => setDateTo(e.target.value)}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group>
                                    <Form.Label>Report Type</Form.Label>
                                    <SelectControl
                                        value={String(reportType)}
                                        onChange={(val) => setReportType(parseInt(val || '3'))}
                                        options={[
                                            { value: '1', label: 'Consumed' },
                                            { value: '2', label: 'Allocated' },
                                            { value: '3', label: 'Consumed & Allocated' }
                                        ]}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3} className="mt-3 mt-md-0">
                                <Button 
                                    type="submit" 
                                    variant="primary"
                                    onClick={handleCustomDateSubmit}
                                >
                                    RETURN REPORT
                                </Button>
                            </Col>
                        </Row>
                    </Form>
                    </Card.Body>
                </Card>
                
                {loadingReport && (
                    <div className="text-center">
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Loading report...</span>
                        </Spinner>
                        <p className="mt-2">Loading report data...</p>
                    </div>
                )}

                {!loadingReport && reportData && (
                    <Card className="glass mb-4">
                        <Card.Body>
                            {/* Units/GBP Toggle */}
                            <div className="mb-4">
                                <Form.Check
                                    type="switch"
                                    id="units-pounds-switch"
                                    label="Units / GBP"
                                    checked={showPounds}
                                    onChange={(e) => setShowPounds(e.target.checked)}
                                />
                            </div>

                            {/* Report Table */}
                            <div className="table-responsive" style={{overflowX: 'auto', WebkitOverflowScrolling: 'touch', display: 'block', width: '100%'}}>
                            <Table striped bordered hover style={{minWidth: '800px', whiteSpace: 'nowrap'}}>
                                <thead>
                                    <tr>
                                        <th className="group">Work Stream</th>
                                        <th className="name">EP</th>
                                        <th className="name">Approver</th>
                                        <th>EST Days</th>
                                        <th>Net {showPounds ? 'GBP' : 'Units'} Actual</th>
                                        {mondays.map((monday, index) => (
                                            <th 
                                                key={index} 
                                                className={`date-col ${activeColumn === index + 1 ? 'active' : ''}`} 
                                                data-id={index + 1}
                                                onClick={() => handleColumnClick(index + 1)}
                                                style={{cursor: 'pointer'}}
                                            >
                                                {monday}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderTableBody()}
                                    {renderTotalsRow()}
                                </tbody>
                            </Table>
                        </div>
                        </Card.Body>
                    </Card>
                )}

                {!loadingReport && !reportData && hasSubmitted && (
                    <Alert variant="info">
                        <h5>No data available</h5>
                        <p>No bookings found for the selected criteria.</p>
                    </Alert>
                )}
                
                <style>{`
                    .table td, .table th {
                        white-space: nowrap;
                        min-width: 80px;
                    }
                    .table .group, .table .name {
                        min-width: 120px;
                    }
                    .table-responsive {
                        overflow-x: auto !important;
                        -webkit-overflow-scrolling: touch !important;
                        display: block !important;
                        width: 100% !important;
                    }
                    @media (max-width: 768px) {
                        .table-responsive {
                            margin: 0 -15px;
                            width: calc(100% + 30px);
                        }
                        .table {
                            display: table !important;
                            width: max-content !important;
                        }
                        .table td, .table th {
                            font-size: 0.875rem;
                            padding: 0.5rem;
                        }
                    }
                `}</style>
            </Container>
        </div>
    );
}

export default CfoBurnReport;
